import { Modal } from "react-bootstrap";
import useFlow from "../hooks/useFlow";
import { clearHubSpotState } from "../utils/hubspot";
import { toast } from "react-toastify";
import PhoneInputWithNumpad from "./PhoneInputWithNumpad";
import { useState, useEffect } from "react";
import "./AskPhoneModal.css";

export default function AskPhoneModal() {
  const {
    showAskPhoneModal,
    setShowAskPhoneModal,
    setShowMapResourceModal,
    setShowOfferingModal,
    saveClientPhone,
    askedForPhone,
    locationPreferences,
    pendingOffering,
  } = useFlow();

  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (showAskPhoneModal) {
      askedForPhone();
    }
  }, [showAskPhoneModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setPhone(""); // Clear phone input when modal closes
    setShowAskPhoneModal(false);
    clearHubSpotState();
    if (pendingOffering) setShowOfferingModal(true);
    else setShowMapResourceModal(true);
  };

  const handleUpdateClientPhone = async () => {
    if (!phone || phone.replace(/\D/g, "").length !== 10) {
      toast.error("Please enter a valid 10-digit phone number", {
        autoClose: 3000,
        position: "top-center",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await saveClientPhone(phone);
      toast.success("Perfect! Thanks for staying connected!", {
        autoClose: 5000,
        position: "top-center",
      });
      setPhone(""); // Clear phone input after successful submission
      setShowAskPhoneModal(false);
      clearHubSpotState();
      if (pendingOffering) setShowOfferingModal(true);
      else setShowMapResourceModal(true);
    } catch (error) {
      toast.error("Failed to update phone number. Please try again.", {
        autoClose: 3000,
        position: "top-center",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPhoneValid = phone.replace(/\D/g, "").length === 10;

  return (
    <>
      <Modal
        show={showAskPhoneModal}
        onHide={handleClose}
        centered
        size="lg"
        className="ask-phone-modal"
      >
        <Modal.Body className="p-0">
          <div className="ask-phone-container">
            {/* Close Button */}
            <button
              className="ask-phone-close-btn"
              data-testid="ask-phone-modal-close-btn"
              onClick={handleClose}
              aria-label="Close modal"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  fill="currentColor"
                />
              </svg>
            </button>

            {/* Header Section */}
            <div className="ask-phone-header">
              <div className="phone-icon">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <h3 className="ask-phone-title">
                Oops! We’re missing your phone number
              </h3>
              {/* <p className="ask-phone-subtitle">
                Oops! Looks like we&apos;re missing your phone number.
              </p> */}
            </div>

            {/* Form Section */}
            <div className="ask-phone-form">
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <PhoneInputWithNumpad
                  value={phone}
                  onChange={setPhone}
                  placeholder="(512) 555-0123"
                  showNumpad={locationPreferences?.showPhoneNumpad}
                  numpadInline
                  inputClassName={`phone-input ${
                    isPhoneValid ? "is-valid" : phone ? "is-invalid" : ""
                  }`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && isPhoneValid) {
                      handleUpdateClientPhone();
                    }
                  }}
                />
                {/* <div className="form-text">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                      fill="currentColor"
                    />
                  </svg>
                  We&apos;ll only use this for important updates and exclusive
                  offers
                </div> */}
              </div>

              {/* Action Buttons */}
              <div className="ask-phone-actions">
                <button
                  className="btn btn-primary btn-lg submit-btn"
                  data-testid="ask-phone-submit-btn"
                  onClick={handleUpdateClientPhone}
                  disabled={!isPhoneValid || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="me-2"
                      >
                        <path
                          d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                          fill="currentColor"
                        />
                      </svg>
                      Update Phone Number
                    </>
                  )}
                </button>

                {/* <button
                  className="btn btn-outline-secondary btn-lg decline-btn"
                  onClick={handleDecline}
                  disabled={isSubmitting}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="me-2"
                  >
                    <path
                      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                      fill="currentColor"
                    />
                  </svg>
                  Maybe Later
                </button> */}
              </div>

              {/* Footer Note */}
              {/* <div className="ask-phone-footer">
                <p className="small text-muted">
                  You can always update your preferences or unsubscribe anytime.
                </p>
              </div> */}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
